import { Effect } from 'effect'
import { topic, type Envelope, type Topic } from './types'

export type PhoenixFrame = readonly [
  joinRef: string | null,
  ref: string | null,
  topic: string,
  event: string,
  payload: unknown
]

export type DecodedFrame = {
  readonly joinRef: string | null
  readonly ref: string | null
  readonly topic: Topic
  readonly event: string
  readonly payload: unknown
}

export function encodePhoenixFrame(frame: PhoenixFrame): string {
  return JSON.stringify(frame)
}

export function encodeJoinFrame(topicValue: Topic, joinRef: string, ref: string): string {
  return encodePhoenixFrame([joinRef, ref, topicValue, 'phx_join', {}])
}

export function encodePushFrame(
  topicValue: Topic,
  joinRef: string,
  ref: string,
  event: string,
  payload: unknown
): string {
  return encodePhoenixFrame([joinRef, ref, topicValue, event, payload])
}

export function encodeLeaveFrame(topicValue: Topic, joinRef: string, ref: string): string {
  return encodePhoenixFrame([joinRef, ref, topicValue, 'phx_leave', {}])
}

export function encodeHeartbeatFrame(ref: string): string {
  // Phoenix transport heartbeat is always sent on topic "phoenix" with no join_ref.
  return encodePhoenixFrame([null, ref, 'phoenix', 'heartbeat', {}])
}

export class PhoenixFrameDecodeError extends Error {
  readonly _tag = 'PhoenixFrameDecodeError' as const

  constructor(readonly cause: unknown) {
    super('failed to decode phoenix websocket frame')
  }
}

export function decodePhoenixFrame(
  data: string | Uint8Array
): Effect.Effect<DecodedFrame, PhoenixFrameDecodeError> {
  return Effect.try({
    try: () => {
      const raw = typeof data == 'string' ? data : new TextDecoder().decode(data)
      const decoded = JSON.parse(raw)
      if (!Array.isArray(decoded) || decoded.length < 5) {
        throw new Error('invalid phoenix frame shape')
      }

      const joinRef = normalizeNullableString(decoded[0])
      const ref = normalizeNullableString(decoded[1])
      const topicValue = decoded[2]
      const event = decoded[3]
      const payload = decoded[4]

      if (typeof topicValue != 'string') {
        throw new Error('invalid topic')
      }
      if (typeof event != 'string') {
        throw new Error('invalid event')
      }

      return {
        joinRef,
        ref,
        topic: topic(topicValue),
        event,
        payload
      } satisfies DecodedFrame
    },
    catch: (error) => new PhoenixFrameDecodeError(error)
  })
}

export function frameToEnvelope(frame: DecodedFrame): Envelope {
  return {
    topic: frame.topic,
    event: frame.event,
    payload: frame.payload,
    joinRef: frame.joinRef,
    ref: frame.ref
  }
}

function normalizeNullableString(value: unknown): string | null {
  if (value == null) {
    return null
  }
  return typeof value == 'string' ? value : null
}
